import {EyeOutlined} from '@ant-design/icons';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  StopOutlined,
  UndoOutlined
} from '@ant-design/icons/lib';
import {Card, Modal, Timeline as _Timeline, Tooltip, Typography} from 'antd';
import {ChargeEvent, ChargeFieldsFragment, Maybe, useChargeEventsQuery} from 'api/generated';
import {AnchorLink} from 'components';
import {Code} from 'components/Code';
import {TimelineChargeMessage} from 'components/TimelineChargeMessage';
import {colors} from 'lib/colors';
import {getEventTooltipMessage} from 'lib/eventUtils';
import {prettifyEvent} from 'lib/utils';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {useRouteMatch} from 'react-router-dom';
import styled from 'styled-components';
import {EventType} from 'types';

const {Text} = Typography;

type Props = {
  charge?: Maybe<ChargeFieldsFragment>;
};

const Timeline = styled(_Timeline)`
  margin: 10px 0 0 3px !important;
  & .ant-timeline-item-last {
    padding-bottom: 0;
    & > .ant-timeline-item-content {
      min-height: auto;
    }
  }
`;

const eventMapping: Record<
  string,
  {title: string | JSX.Element; icon?: any; color?: string; theme?: 'filled'}
> = {
  [EventType.CHARGE_PENDING]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_PENDING)}>Payment started</Tooltip>
    ),
    icon: ClockCircleOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_PENDING_PROCESSING]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_PENDING_PROCESSING)}>
        Payment pending processing
      </Tooltip>
    ),
    icon: ClockCircleOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_UPDATED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_UPDATED)}>Payment updated</Tooltip>
    ),
    icon: InfoCircleOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_AUTHORIZED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_AUTHORIZED)}>
        Payment authorized
      </Tooltip>
    ),
    icon: ClockCircleOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_REFUNDED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_REFUNDED)}>Payment refunded</Tooltip>
    ),
    icon: UndoOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_PARTIALLY_REFUNDED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_PARTIALLY_REFUNDED)}>
        Payment partially refunded
      </Tooltip>
    ),
    icon: UndoOutlined,
    color: colors.secondary
  },
  [EventType.CHARGE_CAPTURED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_CAPTURED)}>Payment captured</Tooltip>
    ),
    icon: CheckCircleFilled,
    color: colors.green
  },
  [EventType.CHARGE_CANCELED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_CANCELED)}>Payment cancelled</Tooltip>
    ),
    icon: StopOutlined,
    color: colors.red
  },
  [EventType.CHARGE_SUCCEEDED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_SUCCEEDED)}>
        Payment succeeded
      </Tooltip>
    ),
    icon: CheckCircleFilled,
    color: colors.green
  },
  [EventType.CHARGE_PAID_OUT]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_PAID_OUT)}>Payout succeeded</Tooltip>
    ),
    icon: CheckCircleFilled,
    color: colors.green
  },
  [EventType.CHARGE_FAILED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_FAILED)}>Payment failed</Tooltip>
    ),
    icon: CloseCircleOutlined,
    color: colors.red
  },
  [EventType.CHARGE_EXPIRED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_EXPIRED)}>Payment expired</Tooltip>
    ),
    icon: CloseCircleOutlined,
    color: colors.red
  },
  [EventType.CHARGE_UPDATED]: {
    title: (
      <Tooltip title={getEventTooltipMessage(EventType.CHARGE_UPDATED)}>Payment updated</Tooltip>
    ),
    icon: InfoCircleOutlined,
    color: colors.secondary
  }
};

// Always put charge.pending first
const eventSorter = (a: ChargeEvent, b: ChargeEvent) => {
  const diff = b.createdAt - a.createdAt;
  if (diff !== 0) return diff;
  return [EventType.CHARGE_PENDING, EventType.PENDING_PROCESSING].includes(a.type as EventType)
    ? 1
    : -1;
};

export const PaymentTimeline: React.FC<Props> = ({charge}) => {
  const match = useRouteMatch<{chargeId: string}>();
  const chargeId = match.params.chargeId;
  const [selectedEvent, setSelectedEvent] = useState<ChargeEvent | null>(null);
  const [isInfoOpen, setInfoOpen] = useState(false);
  const {data, loading, startPolling, stopPolling} = useChargeEventsQuery({variables: {chargeId}});
  const events = data?.chargeEvents.items.slice().sort(eventSorter) || [];
  const lastEvent = events[0];
  const hasPendingEvent =
    (!loading && !lastEvent) || (charge?.updatedAt && charge?.updatedAt !== lastEvent?.createdAt);

  useEffect(() => {
    if (hasPendingEvent) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [hasPendingEvent, startPolling, stopPolling]);

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  return (
    <>
      <Card title="Timeline" size="small" style={{marginBottom: 20}} loading={loading}>
        <Timeline>
          {hasPendingEvent && (
            <Timeline.Item dot={<LoadingOutlined />}>Processing...</Timeline.Item>
          )}
          {events.map((event, i) => {
            const item = eventMapping[event.type];
            return (
              <Timeline.Item
                key={i}
                dot={item.icon ? <item.icon style={{color: item.color}} /> : null}>
                <div>
                  <div>
                    {item.title}{' '}
                    <AnchorLink
                      onClick={() => {
                        setSelectedEvent(event);
                        setInfoOpen(true);
                      }}>
                      <EyeOutlined />
                    </AnchorLink>
                  </div>
                  <div>
                    <TimelineChargeMessage event={event} showLastRefunded={true} />
                  </div>
                  <Text type="secondary" style={{fontSize: '12px'}}>
                    {moment.unix(event.createdAt).format('lll')}
                  </Text>
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Card>
      <Modal
        title="Event data"
        width={800}
        open={isInfoOpen}
        footer={false}
        onCancel={handleInfoClose}>
        {selectedEvent && <Code>{prettifyEvent(selectedEvent)}</Code>}
      </Modal>
    </>
  );
};
